import * as React from 'react';
import Chart from 'react-apexcharts';

import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';

import useAxios from '../utils/useAxios';
import { addGranularSalesStats } from '../actions';
import { SectionCard, EmptyState, currency } from './ui';
import { CHART_COLORS, TRUE_COLORS } from '../theme';
import ShowChartIcon from '@mui/icons-material/ShowChart';

const PERIOD = {
  day:   { format: 'HH:mm', sub: 'hour',  current: 'Hoy',          past: 'Ayer',           best: 'Mejor día',    title: 'del día' },
  week:  { format: 'ddd',   sub: 'day',   current: 'Esta semana',  past: 'Semana pasada',  best: 'Mejor semana', title: 'de la semana' },
  month: { format: 'DD',    sub: 'day',   current: 'Este mes',     past: 'Mes pasado',     best: 'Mejor mes',    title: 'del mes' },
  year:  { format: 'MMM',   sub: 'month', current: 'Este año',     past: 'Año pasado',     best: 'Mejor año',    title: 'del año' },
};

const HomeAreaChart = () => {
  const axios = useAxios();
  const dispatch = useDispatch();

  const homeReceipts = useSelector(state => state.homeReceipts);
  const homeTimeInterval = useSelector(state => state.homeTimeInterval);
  const granularSalesStats = useSelector(state => state.granularSalesStats);

  const [max, setMax] = React.useState(0);
  const period = PERIOD[homeTimeInterval] ?? PERIOD.day;

  React.useEffect(() => {
    const fetchTimeIntervalStats = async () => {
      if (granularSalesStats[homeTimeInterval]) {
        // Seed with 0: Math.max() of an empty list (org with no sales) is -Infinity, which crashes ApexCharts.
        const max = Math.max(0, ...granularSalesStats[homeTimeInterval].map((stat) => stat.total_amount));
        setMax(max);
      }
      // Asked again each time (what was loaded before shows meanwhile): sales deleted since then leave the best day.
      try {
        const { data } = await axios.get('api/receipts/summary/', { params: { granularity: homeTimeInterval, time_series: true } });
        if (cancelled) return;
        dispatch(addGranularSalesStats({ [homeTimeInterval]: data }));
        setMax(Math.max(0, ...data.map((stat) => stat.total_amount)));
      }
      catch (error) {
        console.log(error);
      }
    };
    let cancelled = false;
    fetchTimeIntervalStats();
    return () => { cancelled = true; };
    // granularSalesStats is read, not watched: fetching updates it, which would ask again forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axios, dispatch, homeTimeInterval]);

  const { series, categories, hasSales } = React.useMemo(() => {
    const startDate = dayjs().subtract(1, homeTimeInterval).startOf(homeTimeInterval);
    const endDate = dayjs().endOf(homeTimeInterval);
    const currentStart = endDate.startOf(homeTimeInterval);
    const totalSteps = Math.max(
      endDate.diff(currentStart, period.sub) + 1,
      startDate.diff(startDate.endOf(homeTimeInterval), period.sub) + 1,
    );

    const currentReceipts = homeReceipts.filter((r) => !dayjs(r.date).isBefore(currentStart));
    const pastReceipts = homeReceipts.filter((r) => dayjs(r.date).isBefore(currentStart));

    const categories = [];
    const currentData = [];
    const pastData = [];
    let cummCurrent = 0;
    let cummPast = 0;
    for (let i = 0; i < totalSteps; i++) {
      const label = dayjs(startDate).add(i, period.sub).format(period.format);
      const current = currentReceipts.find((r) => dayjs(r.date).format(period.format) === label);
      const past = pastReceipts.find((r) => dayjs(r.date).format(period.format) === label);
      cummCurrent += current ? Number(current.total_amount) || 0 : 0;
      cummPast += past ? Number(past.total_amount) || 0 : 0;
      categories.push(label);
      currentData.push(Math.round(cummCurrent * 100) / 100);
      pastData.push(Math.round(cummPast * 100) / 100);
    }

    return {
      categories,
      hasSales: cummCurrent > 0 || cummPast > 0 || max > 0,
      series: [
        { type: 'area', name: period.current, data: currentData },
        { type: 'area', name: period.past, data: pastData },
        { type: 'line', name: period.best, data: currentData.map(() => max) },
      ],
    };
  }, [homeReceipts, homeTimeInterval, max, period]);

  const options = React.useMemo(() => ({
    chart: {
      type: 'area',
      fontFamily: 'Inter, sans-serif',
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, dynamicAnimation: { enabled: false } },
    },
    colors: [CHART_COLORS[0], CHART_COLORS[1], CHART_COLORS[2]],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: [2.5, 2, 2], dashArray: [0, 0, 6] },
    fill: {
      type: ['gradient', 'gradient', 'solid'],
      gradient: { shadeIntensity: 1, opacityFrom: 0.28, opacityTo: 0.02, stops: [0, 95] },
      opacity: [1, 1, 1],
    },
    grid: { borderColor: TRUE_COLORS.borderSolid, strokeDashArray: 4, padding: { left: 8, right: 8 } },
    markers: { size: 0, hover: { size: 4 } },
    legend: { position: 'top', horizontalAlign: 'left', fontWeight: 500, markers: { size: 5 }, itemMargin: { horizontal: 12 } },
    xaxis: {
      type: 'category',
      categories,
      tickAmount: Math.min(categories.length, 12),
      labels: { rotate: 0, hideOverlappingLabels: true, style: { colors: TRUE_COLORS.secondary } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: { formatter: (value) => currency(value).replace('.00', ''), style: { colors: TRUE_COLORS.secondary } },
    },
    tooltip: { shared: true, intersect: false, y: { formatter: (value) => currency(value) } },
  }), [categories]);

  return (
    <SectionCard title={`Ventas acumuladas ${period.title}`} subtitle={`${period.current} comparado con ${period.past.toLowerCase()}`}>
      {hasSales ? (
        <Chart series={series} options={options} type="area" height={320} />
      ) : (
        <EmptyState
          icon={<ShowChartIcon />}
          title="Aún no hay ventas en este periodo"
          description="Las ventas que registres en TRUE POS aparecerán aquí en tiempo real."
        />
      )}
    </SectionCard>
  );
};

export default HomeAreaChart;
